import ActionButton from 'components/ActionButton';
import TextInputField from 'components/TextInputField';
import { Controller, useForm } from 'react-hook-form';
import { LOGIN_EMAIL_PLACEHOLDER, loginValidationRules } from '../../constants';
import { LoginTabProps } from './types';
import { useEffect, useRef, useState } from 'react';
import { showError } from 'utils/toastNotifications';
import { AssetUrls } from 'config/assets';
import { sanitizeEmail } from 'utils/inputSanitization';
import PasswordResetModal from 'components/PasswordResetModal/PasswordResetModal';
import { useForgotPasswordMutation } from 'store/services/auth/authApi';
import './LoginTab.css';

const LOGIN_PASSWORD_PLACEHOLDER = 'Password';
const LOGIN_TITLE = 'Welcome Back';
const LOGIN_DESCRIPTION = 'Enter your credentials to login';

const LoginTab = ({ data, isLoading, error, login, initialEmail, initialOpenReset, onSwitchToJoin }: LoginTabProps) => {
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    clearErrors,
  } = useForm({ defaultValues: { email: initialEmail ?? '', password: '' } });

  const [apiError, setApiError] = useState<any>(null);
  const [lockoutError, setLockoutError] = useState<{ message: string; remainingMinutes: number } | null>(null);
  const [showResetModal, setShowResetModal] = useState(Boolean(initialOpenReset));
  // Arriving with the email already filled (the Join tab found an existing
  // account, or the sign-up form's Log in link), the next thing to type is the
  // password (Asana 1219793876039315).
  const passwordRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (initialEmail) passwordRef.current?.focus();
  }, [initialEmail]);
  const [forgotPassword, { isLoading: isResetting }] = useForgotPasswordMutation();

  // Show toast notifications for errors only
  useEffect(() => {
    if (error) {
      // Account lockout (HTTP 423) — show inline message instead of toast
      if (error.status === 423) {
        const remainingMinutes = (error.data as any)?.remainingMinutes || 15;
        setLockoutError({ message: error.data?.message || 'Account temporarily locked', remainingMinutes });
        setApiError(error);
        return;
      }

      // Provide more specific error messages based on the error
      let errorMessage = 'Login failed. Please try again.';

      if (error.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.status === 404) {
        errorMessage = 'No account found with this email address. Please sign up first.';
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      }

      showError(errorMessage);
      setApiError(error); // Store the error to maintain red fields
    }
    // Don't show success notification - user knows they logged in when they reach the dashboard
  }, [error]);

  const onSubmit = (data: { email: string; password: string }) => {
    setApiError(null); // Clear any previous API errors
    setLockoutError(null); // Clear any previous lockout errors
    // Sanitize inputs before submission
    const sanitizedEmail = sanitizeEmail(data.email);
    const sanitizedPassword = data.password; // Passwords should not be altered
    login(sanitizedEmail, sanitizedPassword);
  };

  const handleForgotPassword = async (email: string) => {
    await forgotPassword({ email }).unwrap();
  };

  return (
    <div className="login-tab">
      <div className="login-messages">
        <div className="login-title">
          {LOGIN_TITLE}
        </div>
        <div className="login-description">
          {LOGIN_DESCRIPTION}
        </div>
      </div>
      <div className="login-inputs-wrapper">
        <Controller
          name="email"
          control={control}
          rules={loginValidationRules.email}
          render={({ field }) => (
            <TextInputField
              label={LOGIN_EMAIL_PLACEHOLDER}
              // Bound to the form so initialEmail (router state from the sign-up
              // form, or the Join tab's Log in link) renders; an unbound field
              // never shows react-hook-form's default (Asana 1219793876039315).
              value={field.value}
              error={Boolean(errors.email) || Boolean(apiError)}
              onChange={(value) => {
                // Sanitize email input
                const sanitized = sanitizeEmail(value);
                field.onChange(sanitized);
                // Clear errors when user starts typing
                if (errors.email) clearErrors('email');
                if (apiError) setApiError(null);
                if (lockoutError) setLockoutError(null);
              }}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          rules={loginValidationRules.password}
          render={({ field }) => (
            <TextInputField
              label={LOGIN_PASSWORD_PLACEHOLDER}
              type="password"
              inputRef={passwordRef}
              error={Boolean(errors.password) || Boolean(apiError)}
              onChange={(value) => {
                field.onChange(value);
                // Clear errors when user starts typing
                if (errors.password) clearErrors('password');
                if (apiError) setApiError(null);
                if (lockoutError) setLockoutError(null);
              }}
            />
          )}
        />
        <div className="forgot-password-wrapper">
          <button
            type="button"
            className="forgot-password-link"
            onClick={() => setShowResetModal(true)}
          >
            Forgot Password?
          </button>
        </div>
        <ActionButton
          label="Sign In"
          icon={
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="trademagic-icon"
            />
          }
          isLoading={isLoading}
          onPress={handleSubmit(onSubmit)}
        />
        {onSwitchToJoin && (
          <p className="login-helper-link">
            New to TradeMagic?{' '}
            <button type="button" onClick={() => onSwitchToJoin(getValues('email'))}>
              Sign up
            </button>
          </p>
        )}
        {lockoutError && (
          <div className="login-lockout-message">
            Your account has been temporarily locked due to too many failed login attempts.
            You can try again in {lockoutError.remainingMinutes} minutes, or{' '}
            <button
              type="button"
              className="lockout-reset-link"
              onClick={() => setShowResetModal(true)}
            >
              reset your password
            </button>{' '}
            to unlock immediately.
          </div>
        )}
      </div>
      <PasswordResetModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onSubmit={handleForgotPassword}
        isLoading={isResetting}
        initialEmail={initialEmail}
      />
    </div>
  );
};

export default LoginTab;
